import {
  CURRENCIES,
  LOCALES,
  SUBSCRIPTION_WEEKLY,
  SUBSCRIPTION_MONTHLY,
  SUBSCRIPTION_ANNUAL,
  SCHEDULE_TYPE_ASAP,
  SCHEDULE_TYPE_SCHEDULED,
  ACCESS_CODE_LOGIN_TYPE,
  CLUB_MED_LOGIN_TYPE,
  EMAIL_LOGIN_TYPE,
  HAGGLE_CHECKOUT_TYPE,
  MARKETPLACE_CHECKOUT_TYPE,
  KIOSK_PRICING_CHECKOUT_TYPE,
  MULTI_PRINT_MODE,
  SIZED_PRINT_MODE,
  LEGACY_PRINT_MODE,
  DISABLED_PRINT_MODE,
  LEGACY_THEME,
  CLUBMED_THEME,
  HPX_THEME,
  ICECASTLE_THEME,
  DEFAULT_PAGE_MODE,
  CLUBMDED_PAGE_MODE,
  HAWKS_PAGE_MODE,
  EMAIL_PAGE_MODE,
  GENERIC_POS,
  BILL_TO_ROOM_POS
} from 'shared/utils/Constants';

import {
  STANDARD_ACCOUNT_TYPE,
  GFCREW_ACCOUNT_TYPE,
  SUBJOB_ACCOUNT_TYPE,
  HAWKS_ACCOUNT_TYPE,
  KIOSK_ACCOUNT_TYPE,
  CLUBMED_ACCOUNT_TYPE,
  HPX_ACCOUNT_TYPE,
  MOSTRECENT_MOMENT_ORDER,
  CHRONOLOGICAL_MOMENT_ORDER,
  FILENAME_MOMENT_ORDER,
  CARDS_DOWNLOAD_TYPE,
  PAGES_DOWNLOAD_TYPE,
  CSV_DOWNLOAD_TYPE,
  IMAGE_PRODUCT_TYPE,
  VIDEO_PRODUCT_TYPE,
  OVERLAY_PRODUCT_TYPE,
  KNOCKOUT_PRODUCT_TYPE,
  BACKGROUND_PRODUCT_TYPE,
  VIDEO_STORY_PRODUCT_TYPE,
  THE_36PIX_PRODUCT_TYPE,
  UPGRDIO_PRODUCT_TYPE,
  WEB_CART_PACKAGE_PRODUCT_TYPE,
  BACKGROUND_GRAVITY_LEFT,
  BACKGROUND_GRAVITY_CENTER,
  BACKGROUND_GRAVITY_RIGHT
} from 'app/utils/Constants';

import {
  DISABLE_ADD_CUSTOMER,
  SUB_JOBS,
  HAWKS,
  KIOSK_MODE,
  HPX,
  CLUB_MED,
  DISABLE_UPLOAD,
  MEMORY_CODES,
  QR_CODE_GENERATOR,
  DRIVE_UPLOAD,
  DRIVE_PRINTING,
  CUSTOM_DEFAULTS
} from 'app/components/Accounts/featureFlags';

import * as featureFlags from 'app/components/Accounts/featureFlags';

export const formatOptions = (records, props = {}) => {
  const {
    label,
    value,
    contextId,
    icon,
    group
  } = props;

  let options = records.map(record => formatOption(record, {
    label,
    value,
    contextId,
    icon,
    group
  }));

  // grouped options must be sorted by group
  if (group) {
    options.sort((a, b) => a.group.localeCompare(b.group));
  }

  return options;
}

export const formatOption = (record, props = {}) => {
  const {
    label = 'name',
    value = 'id',
    contextId,
    icon,
    group
  } = props;

  let recordLabel = null;
  let recordIcon = null;
  let recordGroup = null;

  // record is sometimes null in quick edit mode for some reason
  if (record) {
    if (typeof label === 'function') {
      recordLabel = label(record);
    } else {
      recordLabel = record[label];
    }

    if (typeof icon === 'function') {
      recordIcon = icon(record);
    } else {
      recordIcon = icon;
    }

    if (typeof group === 'function') {
      recordGroup = group(record);
    } else {
      recordGroup = group;
    }
  }

  return createOption(recordLabel, record[value], {
    contextId: record[contextId],
    data: record,
    icon: recordIcon,
    group: recordGroup
  });
}

export const createOption = (label, value, props = {}) => {
  const {
    data,
    contextId,
    helperText,
    icon,
    meta,
    group
  } = props;

  return {
    label,
    value,
    data,
    contextId,
    helperText,
    icon,
    meta,
    group
  };
}

// -- reusable options
// if options are needed in multiple places then define them here,
// otherwise just defining them in the component is fine

export const currencyOptions = CURRENCIES.map(({ abbr, symbol }) => {
  return createOption(`${symbol} ${abbr}`, abbr);
});

export const localeOptions = formatOptions(LOCALES, { value: 'locale' });

export const subscriptionOptions = [
  createOption('Weekly', SUBSCRIPTION_WEEKLY),
  createOption('Monthly', SUBSCRIPTION_MONTHLY),
  createOption('Annual', SUBSCRIPTION_ANNUAL)
]

export const scheduleOptions = [
  createOption('Once ASAP', SCHEDULE_TYPE_ASAP),
  createOption('Monthly', SCHEDULE_TYPE_SCHEDULED)
]

export const loginTypeOptions = [
  createOption('ACCESS CODE', ACCESS_CODE_LOGIN_TYPE),
  createOption('EMAIL ADDRESS', EMAIL_LOGIN_TYPE),
  createOption('CLUB MED', CLUB_MED_LOGIN_TYPE)
]

export const posTypeOptions = [
  createOption('Generic', GENERIC_POS),
  createOption('Bill to Room', BILL_TO_ROOM_POS)
]

export const themeOptions = [
  createOption('LEGACY', 'legacy'),
  createOption('CLUBMED', 'clubmed'),
  createOption('HPX', 'hpx'),
  createOption('ICE CASTLE', 'icecstl')
]

export const checkoutTypeOptions = [
  createOption('HAGGLE MODE', HAGGLE_CHECKOUT_TYPE),
  createOption('MARKETPLACE', MARKETPLACE_CHECKOUT_TYPE),
  createOption('KIOSK PRICING', KIOSK_PRICING_CHECKOUT_TYPE)
]

export const printModeOptions = [
  createOption('LEGACY PRINT MODE', LEGACY_PRINT_MODE),
  createOption('MULTI PRINT MODE', MULTI_PRINT_MODE),
  createOption('SIZED PRINT MODE', SIZED_PRINT_MODE),
  createOption('DISABLED PRINT MODE', DISABLED_PRINT_MODE)
]

export const accountTypeOptions = [
  createOption('Generic', STANDARD_ACCOUNT_TYPE, {
    helperText: 'Plain account with no preset features',
    meta: {
      featureFlags: []
    }
  }),

  // GF Crew is an app used by school sports photographers.
  // each GF Crew account represents a photographer
  // and each one has a registration page in the app.
  // customers register themselves and pay a fee,
  // and the GF Crew API creates the customer with our API
  // and stores the charge record
  // (this is the `charge` table, which is unique to GF Crew).
  // to prevent photographers from circumventing the charge,
  // customers cannot be created in the portal
  // (GF Crew takes a cut of the charge, and photographers could
  // otherwise upload and deliver the images without this).
  // GF Crew customers also have "subjects",
  // which represent the players and their team and jerseys.
  // the customer record itself represents the parent that the images
  // will be sent to, and its subjects are generally siblings
  createOption('GF Crew', GFCREW_ACCOUNT_TYPE, {
    helperText: 'Customers are created by the GF Crew app. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // prevent adding customers outside of the GF Crew app
        DISABLE_ADD_CUSTOMER,
      ]
    }
  }),

  // sub jobs inherit their customer records from their "main job",
  // which is set to the job.job_id field.
  // this is for year-long sporting events where the photographer
  // wants to collect a single group of customers from across each game.
  // it was originally developed for the Minnesota Twins
  // and is now being used by AEG and IAAPA.
  // the UI will show sub jobs and disable customer creation.
  // so far these flows have created the customers via their API integration
  createOption('Sub Jobs (AEG, IAAPA)', SUBJOB_ACCOUNT_TYPE, {
    helperText: 'Customers are shared with sub jobs. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // customers will be added via the account's API integration
        DISABLE_ADD_CUSTOMER,

        // show and manage sub jobs
        SUB_JOBS
      ]
    }
  }),

  // the Atlanta Hawks photographers use the tagging app
  // and the customers register themselves on our access code page.
  // this uses the "memory codes" flow,
  // where customers see a QR code in the PWA
  // that they can show to the photographers,
  // and the photographers scan it with the tagging app.
  // the images will be tagged with the memory code
  // and the customer can scan the QR code to get to the access code page.
  // this flow allows both the image upload and the customer registration
  // to find or create the customer and add the moments to their story,
  // so it doesn't matter which happens first.
  // the UI will show each customer's section, row and seat
  createOption('Hawks', HAWKS_ACCOUNT_TYPE, {
    helperText: 'Customers grid shows section, row and seat. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // customers will be added on tagged image upload or registration
        DISABLE_ADD_CUSTOMER,
        DISABLE_UPLOAD,

        // memory codes will appear in the PWA and be parsed on upload
        MEMORY_CODES,

        // enable the QR code generator
        QR_CODE_GENERATOR,

        // show section, row and seat
        HAWKS
      ]
    }
  }),

  // kiosk mode without any special features
  createOption('Kiosk (Generic, Chukka)', KIOSK_ACCOUNT_TYPE, {
    helperText: 'Customer is created via portal or on kiosk login. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // show kiosks and catalogs in the portal
        KIOSK_MODE
      ]
    }
  }),

  // kiosk mode with HPX features
  createOption('HPX Kiosk', HPX_ACCOUNT_TYPE, {
    helperText: 'Kiosk account with HPX API integration. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // show kiosks and catalogs in the portal
        KIOSK_MODE,

        // customers will be created via the HPX API integration
        DISABLE_ADD_CUSTOMER,

        // images will be uploaded to Google Drive
        DRIVE_UPLOAD,
        DISABLE_UPLOAD,

        // prints will be uploaded to Google Drive
        DRIVE_PRINTING,

        // HPX features
        HPX,
        CUSTOM_DEFAULTS
      ]
    }
  }),

  // kiosk mode with Club Med features
  createOption('Club Med Kiosk', CLUBMED_ACCOUNT_TYPE, {
    helperText: 'Kiosk account with Club Med API integration. Feature flags: {featureFlags}',
    meta: {
      featureFlags: [
        // show kiosks and catalogs in the portal
        KIOSK_MODE,

        // customers will be created via the Club Med FTP integration
        DISABLE_ADD_CUSTOMER,

        // Club Med features
        CLUB_MED
      ]
    }
  })
]

export const momentOrderOptions = [
  createOption('Most Recent', MOSTRECENT_MOMENT_ORDER),
  createOption('Chronological', CHRONOLOGICAL_MOMENT_ORDER),
  createOption('Filename', FILENAME_MOMENT_ORDER)
]

export const downloadTypeOptions = [
  createOption('Cards', CARDS_DOWNLOAD_TYPE),
  createOption('Pages', PAGES_DOWNLOAD_TYPE),
  createOption('CSV only', CSV_DOWNLOAD_TYPE)
]

export const featureFlagOptions = Object.keys(featureFlags).map(key => {
  let label = key.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.substr(1, word.length).toLowerCase())
    .join(' ');

  let value = featureFlags[key];

  return createOption(label, value);
});

export const productTypeOptions = [
  createOption('Digital Image', IMAGE_PRODUCT_TYPE),
  createOption('Digital Video', VIDEO_PRODUCT_TYPE),
  createOption('Overlay Image', OVERLAY_PRODUCT_TYPE),
  createOption('Remove Background', KNOCKOUT_PRODUCT_TYPE),
  createOption('Digital Background (legacy)', BACKGROUND_PRODUCT_TYPE),
  createOption('Video Story (obsolete)', VIDEO_STORY_PRODUCT_TYPE),
  createOption('36pix Background (obsolete)', THE_36PIX_PRODUCT_TYPE),
  createOption('Upgrdio Background (obsolete)', UPGRDIO_PRODUCT_TYPE),
  createOption('Web Cart Package (obsolete)', WEB_CART_PACKAGE_PRODUCT_TYPE)
]

export const backgroundGravityOptions = [
  createOption('Left', BACKGROUND_GRAVITY_LEFT),
  createOption('Center', BACKGROUND_GRAVITY_CENTER),
  createOption('Right', BACKGROUND_GRAVITY_RIGHT)
]

export const pageModeOptions = [
  createOption('Default', DEFAULT_PAGE_MODE),
  createOption('Club Med', CLUBMDED_PAGE_MODE),
  createOption('Hawks', HAWKS_PAGE_MODE),
  createOption('Email Address', EMAIL_PAGE_MODE)
]
